import React from "react";
import { Link } from "react-router-dom";
import "../CSS/DocumentTranslation.css";
import heroBg from "../Assets/bgimage.jpg";
import translationMainImg from "../Assets/translation-service.png";
import certifiedImg from "../Assets/certifiedImg.webp";
import { 
  FaAngleRight, 
  FaCheckCircle, 
  FaClock, 
  FaFileAlt, 
  FaLanguage,
  FaGlobe,
  FaUser,
  FaBriefcase,
  FaGavel,
  FaGraduationCap,
  FaHeartbeat,
  FaBuilding,
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaArrowRight,
  FaShieldAlt,
  FaRocket,
  FaHandshake,
  FaStar,
  FaQuoteLeft,
  FaRegFilePdf,
  FaRegCopy,
  FaCertificate
} from "react-icons/fa";

export default function DocumentTranslation() {
  const heroStyle = {
    backgroundImage: `url(${heroBg})`,
  };

  const languageCategories = [
    {
      region: "European Languages",
      languages: ["English", "French", "German", "Spanish", "Italian", "Portuguese", "Dutch", "Russian"],
      icon: <FaGlobe />,
      color: "#3b82f6"
    },
    {
      region: "Asian Languages",
      languages: ["Hindi", "Arabic", "Chinese", "Japanese", "Korean", "Thai", "Vietnamese", "Malay"],
      icon: <FaLanguage />,
      color: "#10b981"
    },
    {
      region: "South Asian Languages",
      languages: ["Tamil", "Telugu", "Kannada", "Malayalam", "Marathi", "Gujarati", "Bengali", "Urdu"],
      icon: <FaUser />,
      color: "#f59e0b"
    }
  ];

  const documentTypes = [
    {
      title: "Educational Documents",
      description: "Degree certificates, diplomas, transcripts, mark sheets, and school records",
      icon: <FaGraduationCap />,
      examples: ["Degree Certificate", "Diploma", "Transcripts", "Mark Sheets", "School Certificates"],
      color: "#3b82f6"
    },
    {
      title: "Personal Documents",
      description: "Birth certificates, marriage certificates, affidavits, and police clearance",
      icon: <FaUser />,
      examples: ["Birth Certificate", "Marriage Certificate", "Affidavits", "PCC", "Medical Records"],
      color: "#10b981"
    },
    {
      title: "Legal Documents",
      description: "Agreements, contracts, court orders, and power of attorney",
      icon: <FaGavel />,
      examples: ["Contracts", "Agreements", "Court Orders", "Power of Attorney", "Wills"],
      color: "#ef4444"
    },
    {
      title: "Commercial Documents",
      description: "Invoices, company registration, MOA, and export documents",
      icon: <FaBriefcase />,
      examples: ["Invoices", "MOA & AOA", "Company Registration", "Export Docs", "Trade Licenses"],
      color: "#f59e0b"
    },
    {
      title: "Medical Documents",
      description: "Medical reports, prescriptions, and health certificates",
      icon: <FaHeartbeat />,
      examples: ["Medical Reports", "Prescriptions", "Health Certificates", "Vaccination Records"],
      color: "#ec489a"
    },
    {
      title: "Corporate Documents",
      description: "Annual reports, financial statements, and business proposals",
      icon: <FaBuilding />,
      examples: ["Annual Reports", "Financial Statements", "Business Proposals", "Audit Reports"],
      color: "#8b5cf6"
    }
  ];

  const whyChooseUs = [
    {
      title: "Certified Translators",
      description: "Native, certified translators with subject matter expertise",
      icon: <FaCertificate />
    },
    {
      title: "100+ Languages",
      description: "Translation services in over 100 languages worldwide",
      icon: <FaLanguage />
    },
    {
      title: "Fast Turnaround",
      description: "24-48 hour delivery with express options available",
      icon: <FaRocket />
    },
    {
      title: "Confidentiality",
      description: "100% secure and confidential document handling",
      icon: <FaShieldAlt />
    },
    {
      title: "Notarization Available",
      description: "Certified and notarized translations for legal use",
      icon: <FaHandshake />
    },
    {
      title: "Competitive Pricing",
      description: "Affordable rates with no hidden charges",
      icon: <FaStar />
    }
  ];

  const processSteps = [
    { step: "01", title: "Share Documents", description: "Upload or share your documents via email/WhatsApp" },
    { step: "02", title: "Get Quote", description: "Receive a competitive price quote instantly" },
    { step: "03", title: "Translation", description: "Expert translators work on your documents" },
    { step: "04", title: "Quality Check", description: "Multiple rounds of proofreading and verification" },
    { step: "05", title: "Delivery", description: "Get soft copy + hard copy with certification" }
  ];

  const testimonials = [
    {
      name: "Priya Sharma",
      role: "Study Abroad Student",
      text: "Excellent translation service! They translated my degree certificates for my Canada visa application. Fast, accurate, and reasonably priced.",
      rating: 5
    },
    {
      name: "Rahul Mehta",
      role: "Business Owner",
      text: "Very professional team. They translated our company documents for international expansion. Highly recommended!",
      rating: 5
    }
  ];

  return (
    <div className="translation-page">

      {/* HERO SECTION */}
      <section className="translation-hero-section" style={heroStyle}>
        <div className="translation-hero-overlay" />
        <div className="translation-hero-inner">
          <div className="translation-hero-content">
            <div className="translation-hero-badge">DOCUMENT TRANSLATION</div>
            <h1 className="translation-hero-title">Professional Document Translation Services</h1>
            <p className="translation-hero-subtitle">Accurate, Certified & Fast Translation in 100+ Languages</p>
            <nav className="translation-hero-breadcrumb" aria-label="Breadcrumb">
              <ul>
                <li><Link to="/">Home</Link></li>
                <li><FaAngleRight /></li>
                <li className="translation-crumb-current">Document Translation</li>
              </ul>
            </nav>
          </div>
        </div>
      </section>

      {/* INTRODUCTION SECTION */}
      <section className="translation-intro-section">
        <div className="translation-container">
          <div className="translation-intro-grid">
            <div className="translation-intro-text">
              <span className="translation-section-tag">WHY CHOOSE US</span>
              <h2 className="translation-section-title">Accurate & Certified Translation Services</h2>
              <p className="translation-intro-paragraph">
                We provide professional document translation services for legal, personal, educational, and business purposes. 
                Our team of certified translators ensures accuracy, confidentiality, and fast delivery for all your translation needs.
              </p>
              
              <div className="translation-stats-grid">
                <div className="translation-stat-card">
                  <span className="translation-stat-number">100+</span>
                  <span className="translation-stat-label">Languages</span>
                </div>
                <div className="translation-stat-card">
                  <span className="translation-stat-number">5000+</span>
                  <span className="translation-stat-label">Documents Translated</span>
                </div>
                <div className="translation-stat-card">
                  <span className="translation-stat-number">24-48</span>
                  <span className="translation-stat-label">Hours Delivery</span>
                </div>
                <div className="translation-stat-card">
                  <span className="translation-stat-number">100%</span>
                  <span className="translation-stat-label">Satisfaction</span>
                </div>
              </div>

              <div className="translation-contact-mini-cards">
                <a href="tel:+916393244687" className="translation-contact-mini-card">
                  <FaPhoneAlt />
                  <div>
                    <span>Call Us</span>
                    <strong>+91 6393244687</strong>
                  </div>
                </a>
                <a href="mailto:info@apostilleserviceinmumbai.com" className="translation-contact-mini-card">
                  <FaEnvelope />
                  <div>
                    <span>Email Us</span>
                    <strong>info@apostilleserviceinmumbai.com</strong>
                  </div>
                </a>
              </div>
            </div>
            <div className="translation-intro-image">
              <img src={translationMainImg} alt="Document Translation Services" />
            </div>
          </div>
        </div>
      </section>

      {/* LANGUAGES SECTION */}
      <section className="translation-languages-section">
        <div className="translation-container">
          <div className="translation-section-header">
            <span className="translation-section-tag">LANGUAGES WE COVER</span>
            <h2 className="translation-section-title">Translation in 100+ Languages</h2>
            <p className="translation-section-description">We translate to and from any language pair you need</p>
          </div>
          
          <div className="translation-languages-grid">
            {languageCategories.map((cat, index) => (
              <div className="translation-language-category" key={index} style={{ borderTopColor: cat.color }}>
                <div className="translation-category-header">
                  <div className="translation-category-icon" style={{ background: `${cat.color}15`, color: cat.color }}>
                    {cat.icon}
                  </div>
                  <h3>{cat.region}</h3>
                </div>
                <div className="translation-language-tags">
                  {cat.languages.map((lang, i) => (
                    <span key={i} className="translation-language-tag">{lang}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DOCUMENT TYPES SECTION */}
      <section className="translation-documents-section">
        <div className="translation-container">
          <div className="translation-section-header">
            <span className="translation-section-tag">DOCUMENTS WE TRANSLATE</span>
            <h2 className="translation-section-title">Comprehensive Document Translation</h2>
            <p className="translation-section-description">We handle all types of documents for various purposes</p>
          </div>

          <div className="translation-documents-grid">
            {documentTypes.map((doc, index) => (
              <div className="translation-document-card" key={index}>
                <div className="translation-document-icon" style={{ background: `${doc.color}15`, color: doc.color }}>
                  {doc.icon}
                </div>
                <h3>{doc.title}</h3>
                <p>{doc.description}</p>
                <div className="translation-document-examples">
                  {doc.examples.map((example, i) => (
                    <span key={i} className="translation-example-tag" style={{ borderColor: doc.color }}>
                      {example}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US SECTION */}
      <section className="translation-whyus-section">
        <div className="translation-container">
          <div className="translation-section-header translation-section-header-light">
            <span className="translation-section-tag">WHY CHOOSE US</span>
            <h2 className="translation-section-title">Why Clients Trust Our Translation Services</h2>
            <p className="translation-section-description">We deliver excellence with every translation project</p>
          </div>

          <div className="translation-whyus-grid">
            {whyChooseUs.map((item, index) => (
              <div className="translation-whyus-card" key={index}>
                <div className="translation-whyus-icon">{item.icon}</div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS SECTION */}
      <section className="translation-process-section">
        <div className="translation-container">
          <div className="translation-section-header">
            <span className="translation-section-tag">HOW IT WORKS</span>
            <h2 className="translation-section-title">Simple Translation Process</h2>
            <p className="translation-section-description">Get your documents translated in 5 easy steps</p>
          </div>

          <div className="translation-process-steps">
            {processSteps.map((step, index) => (
              <div className="translation-process-step" key={index}>
                <div className="translation-step-number">{step.step}</div>
                <div className="translation-step-content">
                  <h4>{step.title}</h4>
                  <p>{step.description}</p>
                </div>
                {index < processSteps.length - 1 && <div className="translation-step-connector" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CERTIFICATION SECTION */}
      <section className="translation-cert-section">
        <div className="translation-container">
          <div className="translation-cert-grid">
            <div className="translation-cert-content">
              <span className="translation-section-tag">CERTIFIED TRANSLATIONS</span>
              <h2>Get Notarized & Certified Translations</h2>
              <p>
                Our certified translations are accepted by government authorities, 
                embassies, universities, and courts worldwide. We provide:
              </p>
              <ul>
                <li><FaCheckCircle /> Translator's declaration & certification</li>
                <li><FaCheckCircle /> Notarization available on request</li>
                <li><FaCheckCircle /> Sworn translations for legal purposes</li>
                <li><FaCheckCircle /> Hard copy + soft copy delivery</li>
              </ul>
              <Link to="/contact" className="translation-cert-btn">
                Request Certified Translation <FaArrowRight />
              </Link>
            </div>
            <div className="translation-cert-image">
              <img 
                src={certifiedImg} 
                alt="Certified Translation" 
                className="translation-cert-img"
              />
              <div className="translation-cert-badge">CERTIFIED</div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS SECTION */}
      <section className="translation-testimonials-section">
        <div className="translation-container">
          <div className="translation-section-header">
            <span className="translation-section-tag">TESTIMONIALS</span>
            <h2 className="translation-section-title">What Our Clients Say</h2>
            <p className="translation-section-description">Real feedback from satisfied customers</p>
          </div>

          <div className="translation-testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div className="translation-testimonial-card" key={index}>
                <FaQuoteLeft className="translation-quote-icon" />
                <p className="translation-testimonial-text">{testimonial.text}</p>
                <div className="translation-testimonial-rating">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <FaStar key={i} className="translation-star-filled" />
                  ))}
                </div>
                <h4>{testimonial.name}</h4>
                <span className="translation-testimonial-role">{testimonial.role}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="translation-cta-section">
        <div className="translation-container">
          <div className="translation-cta-content">
            <h2 className="translation-cta-title">Need Document Translation?</h2>
            <p className="translation-cta-description">
              Get accurate, certified translations delivered fast. Contact us for a free quote.
            </p>
            <div className="translation-cta-buttons">
              <Link to="/contact" className="translation-btn-primary">
                Get Free Quote <FaArrowRight />
              </Link>
              <a href="tel:+916393244687" className="translation-btn-secondary">
                <FaPhoneAlt /> Call Now
              </a>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}